
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, Download, ExternalLink, AlertCircle } from 'lucide-react';
import { useSitemap, useSitemapGeneration } from '@/hooks/useSitemap';
import { enhancedSitemapService } from '@/services/enhancedSitemapService';
import { useSitemapAutoUpdate } from '@/hooks/useSitemapAutoUpdate';
import { toast } from 'sonner';

const SitemapGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { data: sitemapContent, refetch: generateSitemap } = useSitemap();
  const { data: autoUpdateData, isLoading: isAutoUpdating } = useSitemapAutoUpdate();

  const handleGenerateSitemap = async () => {
    setIsGenerating(true);
    try {
      await enhancedSitemapService.saveSitemapToFile();
      const result = await generateSitemap();
      if (result.data) {
        toast.success('Enhanced sitemap generated! Check console for XML content.');
        console.log('📄 Enhanced Sitemap XML with Blog Posts:');
        console.log(result.data);
        console.log('💡 Copy the above XML content to public/sitemap.xml');
      }
    } catch (error) {
      toast.error('Failed to generate sitemap');
      console.error('Sitemap generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (sitemapContent) {
      try {
        await navigator.clipboard.writeText(sitemapContent);
        toast.success('Sitemap XML copied to clipboard!');
      } catch (error) {
        toast.error('Failed to copy to clipboard');
      }
    }
  };

  return (
    <div className="space-y-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Sitemap Generator
          </CardTitle>
          <CardDescription>
            Generate an updated XML sitemap that includes all static pages and dynamic blog posts.
            The sitemap will be automatically formatted for search engines.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button 
              onClick={handleGenerateSitemap}
              disabled={isGenerating}
              className="flex-1"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate Now
                </>
              )}
            </Button>
            
            {sitemapContent && (
              <Button 
                onClick={copyToClipboard}
                variant="outline"
                size="default"
              >
                Copy XML
              </Button>
            )}
          </div>
          
          {sitemapContent && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Sitemap Generated Successfully!</span>
              </div>
              <p className="text-sm text-green-700 mb-3">
                Your sitemap has been generated with blog posts included.
                Copy the XML content and update <code>public/sitemap.xml</code>.
              </p>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open('https://seniorthrive.com/sitemap.xml', '_blank')}
                >
                  <ExternalLink className="mr-1 h-3 w-3" />
                  View Live Sitemap
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-lg">Auto-Update Status</CardTitle>
          <CardDescription>
            Automatic sitemap updates run daily to keep blog posts current.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isAutoUpdating ? (
            <div className="flex items-center gap-2 text-blue-600">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Checking for updates...</span>
            </div>
          ) : autoUpdateData ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-600">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">
                  {autoUpdateData.success ? 'Auto-update active' : 'Auto-update failed'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Last checked: {new Date(autoUpdateData.lastGenerated).toLocaleString()}
              </p>
              {!autoUpdateData.success && (
                <p className="text-sm text-red-600">
                  Error: {autoUpdateData.error}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Auto-update system is initializing...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SitemapGenerator;
